import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Info, CreditCard, Image, Key, ExternalLink, CheckCircle2, XCircle, 
  Copy, Zap, Shield, AlertTriangle, RefreshCw, Loader2 
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusPill = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-mono ${ok ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400' : 'bg-amber-500/10 border border-amber-500/30 text-amber-400'}`}>
    {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
    {label}
  </span>
);

function FalKeyRotator() {
  const [newKey, setNewKey] = useState('');
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/config/fal-status`, { withCredentials: true })
      .then(r => setStatus(r.data))
      .catch(() => setStatus({ configured: false }));
  }, []);

  const rotate = async () => {
    if (!newKey.includes(':')) { toast.error('Key must be id:secret format'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/config/fal-rotate`, { key: newKey }, { withCredentials: true });
      toast.success('FAL_KEY rotated — live now');
      setStatus({ configured: true, masked: r.data.masked });
      setNewKey('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rotation failed');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="fal-rotator">
      {status?.configured && (
        <p className="text-[11px] font-mono text-emerald-400 mb-2">Current: {status.masked}</p>
      )}
      <div className="flex gap-2">
        <Input
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          placeholder="paste new FAL_KEY (id:secret)"
          className="bg-[#1E1E24] border-[#2E2E34] text-white text-xs font-mono h-9"
          type="password"
          data-testid="fal-rotator-input"
        />
        <Button onClick={rotate} disabled={busy || !newKey} size="sm" className="bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90" data-testid="fal-rotator-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Key className="w-4 h-4 mr-1" /> Rotate</>}
        </Button>
      </div>
    </div>
  );
}

// -------- Admin Help & Setup Info --------
export function AdminHelp() {
  const [pay, setPay] = useState(null);
  const [assets, setAssets] = useState(null);
  const [checking, setChecking] = useState(false);
  const [testPrompt, setTestPrompt] = useState('cyan robot mascot, isolated');

  const checkAll = async () => {
    setChecking(true);
    try {
      const p = await axios.get(`${API}/payments/config`);
      setPay(p.data);
    } catch (e) {
      setPay({ error: e.response?.data?.detail || e.message });
    }
    try {
      const a = await axios.post(`${API}/assets/generate`,
        { prompt: testPrompt, type: 'sprite', style: 'pixel' },
        { withCredentials: true }
      );
      setAssets({ ok: true, url: a.data?.asset?.url, provider: a.data?.asset?.provider, remaining: a.data?.remaining });
    } catch (e) {
      setAssets({ ok: false, error: e.response?.data?.detail || e.message, status: e.response?.status });
    }
    setChecking(false);
  };

  useEffect(() => {
    // Fetch pay config on mount but not assets (that costs quota)
    axios.get(`${API}/payments/config`).then(r => setPay(r.data)).catch(e => setPay({ error: e.message }));
  }, []);

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success('Copied'); };

  return (
    <div className="max-w-4xl space-y-6" data-testid="admin-help">
      <div>
        <h3 className="text-lg font-black text-white flex items-center gap-2">
          <Info className="w-5 h-5 text-[#06B6D4]" /> Admin Setup & Info
          <span className="text-xs font-mono text-zinc-500 uppercase">Admin only</span>
        </h3>
        <p className="text-xs text-zinc-500 mt-1">
          Everything an admin needs to know about the payment, asset, and AI providers wired into PlayMold.
        </p>
      </div>

      {/* Stripe Section */}
      <section className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl p-5" data-testid="admin-stripe-info">
        <div className="flex items-start justify-between gap-4 mb-3">
          <h4 className="text-md font-bold text-white flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-[#06B6D4]" /> Stripe Payments
          </h4>
          {pay && !pay.error ? <StatusPill ok label={`ACTIVE · ${pay.mode?.toUpperCase() || 'TEST'} · ${pay.plans?.length || 0} plans`} /> : <StatusPill ok={false} label="CHECK CONFIG" />}
        </div>

        <div className="space-y-3 text-sm text-zinc-300">
          <p>
            <strong className="text-white">Everything is wired.</strong> The Stripe sandbox is provisioned, catalog products (Pro & Studio, monthly + yearly) are created, prices published via lookup keys, webhook endpoint verifies signatures, checkout success/cancel flows land on branded pages, and user tier is auto-upgraded on paid webhook or status-poll.
          </p>

          <div className="bg-[#050505] rounded p-3 border border-[#1E1E24]">
            <p className="text-xs font-mono text-zinc-500 uppercase mb-2">What admin should know:</p>
            <ul className="list-disc pl-5 space-y-1.5 text-xs text-zinc-400">
              <li><strong className="text-white">Mode:</strong> Currently in <span className="text-amber-400 font-mono">{pay?.mode?.toUpperCase() || 'TEST'}</span> mode. To go live, claim the sandbox at your Stripe onboarding URL (see below) or provide production keys.</li>
              <li><strong className="text-white">Publishable key</strong> (safe to expose): <code className="text-[#06B6D4] break-all">{pay?.publishable_key?.slice(0, 32) || 'not loaded'}...</code></li>
              <li><strong className="text-white">Prices</strong> are defined server-side via `lookup_key` — frontend never sends amounts. Update in `setup_stripe.py`.</li>
              <li><strong className="text-white">Webhook URL</strong> to configure in Stripe Dashboard once live: <code className="text-[#06B6D4]">https://YOUR-DOMAIN/api/stripe/webhook</code></li>
              <li><strong className="text-white">Automatic tax:</strong> Enabled by default with a fallback if unsupported in a country.</li>
              <li><strong className="text-white">Refunds/cancellations</strong> flow through Stripe Dashboard → subscription events → webhook auto-downgrades user to free tier.</li>
            </ul>
          </div>

          <div className="flex gap-2 flex-wrap">
            <a href="https://dashboard.stripe.com/test/dashboard" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs bg-white/5 border border-[#1E1E24] hover:bg-white/10 text-zinc-300">
              <ExternalLink className="w-3 h-3" /> Stripe Dashboard (Test)
            </a>
            <a href="https://dashboard.stripe.com/dashboard" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs bg-white/5 border border-[#1E1E24] hover:bg-white/10 text-zinc-300">
              <ExternalLink className="w-3 h-3" /> Stripe Dashboard (Live)
            </a>
          </div>

          {pay?.plans?.length > 0 && (
            <div className="bg-[#050505] rounded p-3 border border-[#1E1E24]">
              <p className="text-xs font-mono text-zinc-500 uppercase mb-2">Live catalog</p>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-zinc-500">
                    <th className="text-left py-1">Plan</th>
                    <th className="text-left py-1">Tier</th>
                    <th className="text-right py-1">Amount</th>
                    <th className="text-right py-1">Interval</th>
                  </tr>
                </thead>
                <tbody>
                  {pay.plans.map(p => (
                    <tr key={p.plan_id} className="border-t border-[#1E1E24]">
                      <td className="py-1 font-mono text-zinc-300">{p.plan_id}</td>
                      <td className="py-1 text-zinc-400">{p.tier}</td>
                      <td className="py-1 text-right text-white">${p.amount} {p.currency}</td>
                      <td className="py-1 text-right text-zinc-400">{p.interval || 'once'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* fal.ai Section */}
      <section className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl p-5" data-testid="admin-falai-info">
        <div className="flex items-start justify-between gap-4 mb-3">
          <h4 className="text-md font-bold text-white flex items-center gap-2">
            <Image className="w-4 h-4 text-[#06B6D4]" /> fal.ai Asset Generation
          </h4>
          {assets == null ? <span className="text-xs text-zinc-500 font-mono">NOT TESTED</span> : <StatusPill ok={assets.ok} label={assets.ok ? 'WORKING' : `FAILED (${assets.status || 'error'})`} />}
        </div>

        <div className="space-y-3 text-sm text-zinc-300">
          <p>
            <strong className="text-white">Fully wired.</strong> Sprites (128²), textures (seamless tileable) and 3D concept art are generated by <code className="text-[#06B6D4]">fal-ai/flux/schnell</code>. Style presets: pixel / realistic / toon / auto. Quota is atomically rolled back on failure.
          </p>

          <div className="bg-[#050505] rounded p-3 border border-[#1E1E24]">
            <p className="text-xs font-mono text-zinc-500 uppercase mb-2">How to connect fal.ai (2-minute setup)</p>
            <ol className="list-decimal pl-5 space-y-1.5 text-xs text-zinc-400">
              <li>Go to <a className="text-[#06B6D4] hover:underline" href="https://fal.ai/dashboard/keys" target="_blank" rel="noreferrer">fal.ai/dashboard/keys</a> (create a free account if needed).</li>
              <li>Create an API key. Copy it — format: <code>&lt;keyid&gt;:&lt;secret&gt;</code>.</li>
              <li>SSH into the pod (or use file editor). Open <code className="text-[#06B6D4]">/app/backend/.env</code>.</li>
              <li>Set <code className="text-[#06B6D4]">FAL_KEY=your_key_here</code> (already set on this env — you can rotate).</li>
              <li>Top up balance at <a className="text-[#06B6D4] hover:underline" href="https://fal.ai/dashboard/billing" target="_blank" rel="noreferrer">fal.ai/dashboard/billing</a> — pay-as-you-go, ~$0.003 per FLUX-schnell image.</li>
              <li>Run <code className="text-[#06B6D4]">sudo supervisorctl restart backend</code> to pick up the new key.</li>
            </ol>
          </div>

          <div className="bg-[#050505] rounded p-3 border border-[#1E1E24]">
            <p className="text-xs font-mono text-zinc-500 uppercase mb-2">Rotate FAL_KEY directly</p>
            <p className="text-xs text-zinc-400 mb-2">Paste a new key from fal.ai/dashboard/keys. Applies instantly + saved to backend/.env.</p>
            <FalKeyRotator />
          </div>

          <div className="bg-[#050505] rounded p-3 border border-[#1E1E24]">
            <p className="text-xs font-mono text-zinc-500 uppercase mb-2">Live test — generate an asset now</p>
            <div className="flex gap-2 mb-2">
              <Input
                value={testPrompt}
                onChange={(e) => setTestPrompt(e.target.value)}
                placeholder="Describe an asset…"
                className="bg-[#1E1E24] border-[#2E2E34] text-white text-sm h-9"
                data-testid="admin-falai-prompt"
              />
              <Button onClick={checkAll} disabled={checking} className="bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90" data-testid="admin-falai-test">
                {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Zap className="w-4 h-4 mr-1" /> Test</>}
              </Button>
            </div>
            {assets?.ok && assets.url && (
              <div className="mt-2">
                <img src={assets.url} alt="Test asset" className="w-32 h-32 object-cover rounded border border-[#1E1E24]" />
                <p className="text-[10px] font-mono text-zinc-500 mt-1">Provider: {assets.provider} · Remaining today: {assets.remaining}</p>
              </div>
            )}
            {assets?.ok === false && (
              <div className="mt-2 p-2 bg-red-500/5 border border-red-500/30 rounded text-xs text-red-400 font-mono">
                {assets.error}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* AI (Emergent LLM) Section */}
      <section className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl p-5" data-testid="admin-llm-info">
        <div className="flex items-start justify-between gap-4 mb-3">
          <h4 className="text-md font-bold text-white flex items-center gap-2">
            <Zap className="w-4 h-4 text-[#06B6D4]" /> AI Copilot & Image Gen (Emergent LLM Key)
          </h4>
          <StatusPill ok label="ACTIVE" />
        </div>
        <div className="space-y-3 text-sm text-zinc-300">
          <p>
            <strong className="text-white">One key powers three providers.</strong> The AI Copilot uses OpenAI GPT-4/5 and Claude Sonnet. OG social share images and generated logos use Gemini Nano Banana. Localization (FAQ/Tutorial in 10 languages) uses GPT-4.1-mini.
          </p>
          <div className="bg-[#050505] rounded p-3 border border-[#1E1E24]">
            <p className="text-xs font-mono text-zinc-500 uppercase mb-2">Info</p>
            <ul className="list-disc pl-5 space-y-1.5 text-xs text-zinc-400">
              <li>Key stored server-side only as <code className="text-[#06B6D4]">EMERGENT_LLM_KEY</code> — never exposed to the frontend.</li>
              <li>Top up balance via <strong>Profile → Manage plan → Universal Key → Add Balance</strong> in the Emergent app.</li>
              <li>All translation cache entries are permanent once generated. FAQ/Tutorial were pre-warmed at deploy time — 20/20 cached.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Global refresh */}
      <div className="flex justify-end">
        <Button variant="outline" onClick={checkAll} disabled={checking} className="border-[#1E1E24]" data-testid="admin-help-refresh">
          {checking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Refresh all statuses
        </Button>
      </div>
    </div>
  );
}
